/**
 * Groups Store (Pinia)
 * Manages groups state, actions, and business logic
 */

import { defineStore } from 'pinia';
import { ref, computed, readonly } from 'vue';
import { useAuthStore } from './auth';
import { groupService, GroupError, type GroupListParams } from '../services/groupService';
import type { 
  Group, 
  GroupMember, 
  CreateGroupData, 
  UpdateGroupData,
  AddMemberData 
} from '../types/group';

export const useGroupsStore = defineStore('groups', () => {
  // Get auth store for current user
  const authStore = useAuthStore();
  
  // State
  const groups = ref<Group[]>([]);
  const currentGroup = ref<Group | null>(null);
  const members = ref<GroupMember[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  
  // Pagination state
  const currentPage = ref(1);
  const totalPages = ref(1);
  const pageSize = ref(20);
  const totalItems = ref(0);
  
  // Filter state
  const searchQuery = ref('');
  const showPersonalGroups = ref(true);
  const sortBy = ref<'name' | 'created_at' | 'updated_at'>('created_at');
  const sortOrder = ref<'asc' | 'desc'>('desc');
  
  // Getters
  const personalGroups = computed(() => 
    groups.value.filter(g => g.is_personal)
  );
  
  const regularGroups = computed(() => 
    groups.value.filter(g => !g.is_personal)
  );
  
  const filteredGroups = computed(() => {
    let result = [...groups.value];
    
    // Filter by personal/regular
    if (!showPersonalGroups.value) {
      result = result.filter(g => !g.is_personal);
    }
    
    // Filter by search query
    if (searchQuery.value) {
      const query = searchQuery.value.toLowerCase();
      result = result.filter(g => 
        g.name.toLowerCase().includes(query) ||
        g.description?.toLowerCase().includes(query)
      );
    }
    
    return result;
  });
  
  const isGroupOwner = computed(() => {
    if (!currentGroup.value || !authStore.user) return false;
    return currentGroup.value.owner_id === authStore.user.id;
  });
  
  const isGroupAdmin = computed(() => {
    if (!currentGroup.value || !authStore.user) return false;
    const member = members.value.find(m => m.user_id === authStore.user?.id);
    return member?.role === 'admin' || currentGroup.value?.owner_id === authStore.user?.id;
  });
  
  const canManageGroup = computed(() => {
    return isGroupOwner.value || isGroupAdmin.value;
  });
  
  const canDeleteGroup = computed(() => {
    return isGroupOwner.value && currentGroup.value && !currentGroup.value.is_personal;
  });
  
  // Actions
  const setLoading = (loading: boolean) => {
    isLoading.value = loading;
  };
  
  const setError = (errorMessage: string | null) => {
    error.value = errorMessage;
  };
  
  const clearError = () => {
    error.value = null;
  };
  
  /**
   * Fetch groups from the API
   */
  const fetchGroups = async (params?: GroupListParams) => {
    try {
      setLoading(true);
      clearError();
      
      const mergedParams: GroupListParams = {
        page: currentPage.value,
        limit: pageSize.value,
        search: searchQuery.value || undefined,
        is_personal: showPersonalGroups.value ? undefined : false,
        sort_by: sortBy.value,
        sort_order: sortOrder.value,
        ...params
      };
      
      const response = await groupService.listGroups(mergedParams);
      
      groups.value = response.groups;
      totalItems.value = response.total;
      totalPages.value = response.total_pages;
      currentPage.value = response.page;
      
      return response;
    } catch (err) {
      if (err instanceof GroupError) {
        if (err.isUnauthorized) {
          setError('You are not authorized to view groups. Please log in.');
        } else {
          setError(err.message || 'Failed to fetch groups.');
        }
      } else {
        setError('An unexpected error occurred while fetching groups.');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Create a new group
   */
  const createGroup = async (data: CreateGroupData) => {
    try {
      setLoading(true);
      clearError();
      
      const newGroup = await groupService.createGroup(data);
      
      // Add the new group to the list
      groups.value.unshift(newGroup);
      totalItems.value++;
      
      return newGroup;
    } catch (err) {
      if (err instanceof GroupError) {
        if (err.isValidationError) {
          setError('Please check your group information and try again.');
        } else if (err.isConflict) {
          setError('A group with this name already exists.');
        } else {
          setError(err.message || 'Failed to create group.');
        }
      } else {
        setError('An unexpected error occurred while creating the group.');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Get a specific group by ID
   */
  const fetchGroup = async (id: number) => {
    try {
      setLoading(true);
      clearError();
      
      const group = await groupService.getGroup(id);
      currentGroup.value = group;
      
      // Update the group in the list if it exists
      const index = groups.value.findIndex(g => g.id === id);
      if (index !== -1) {
        groups.value[index] = group;
      }
      
      return group;
    } catch (err) {
      if (err instanceof GroupError) {
        if (err.isNotFound) {
          setError('Group not found.');
        } else if (err.isForbidden) {
          setError('You do not have permission to view this group.');
        } else {
          setError(err.message || 'Failed to fetch group details.');
        }
      } else {
        setError('An unexpected error occurred while fetching the group.');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Update a group
   */
  const updateGroup = async (id: number, data: UpdateGroupData) => {
    try {
      setLoading(true);
      clearError();
      
      const updatedGroup = await groupService.updateGroup(id, data);
      
      // Update the group in the list
      const index = groups.value.findIndex(g => g.id === id);
      if (index !== -1) {
        groups.value[index] = updatedGroup;
      }
      
      // Update current group if it's the same
      if (currentGroup.value?.id === id) {
        currentGroup.value = updatedGroup;
      }
      
      return updatedGroup;
    } catch (err) {
      if (err instanceof GroupError) {
        if (err.isForbidden) {
          setError('You do not have permission to update this group.');
        } else if (err.isConflict) {
          setError('A group with this name already exists.');
        } else if (err.isValidationError) {
          setError('Please check your group information and try again.');
        } else {
          setError(err.message || 'Failed to update group.');
        }
      } else {
        setError('An unexpected error occurred while updating the group.');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Delete a group
   */
  const deleteGroup = async (id: number) => {
    try {
      setLoading(true);
      clearError();
      
      await groupService.deleteGroup(id);
      
      // Remove the group from the list
      groups.value = groups.value.filter(g => g.id !== id);
      totalItems.value--;
      
      // Clear current group if it was deleted
      if (currentGroup.value?.id === id) {
        currentGroup.value = null;
        members.value = [];
      }
      
    } catch (err) {
      if (err instanceof GroupError) {
        if (err.isForbidden) {
          setError('You do not have permission to delete this group.');
        } else if (err.isValidationError && currentGroup.value?.is_personal) {
          setError('Personal groups cannot be deleted.');
        } else {
          setError(err.message || 'Failed to delete group.');
        }
      } else {
        setError('An unexpected error occurred while deleting the group.');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Add a member to a group
   */
  const addMember = async (groupId: number, data: AddMemberData) => {
    try {
      setLoading(true);
      clearError();
      
      await groupService.addMember(groupId, data);
      
      // Refresh members list if we're viewing this group
      if (currentGroup.value?.id === groupId) {
        await fetchMembers(groupId);
      }
      
    } catch (err) {
      if (err instanceof GroupError) {
        if (err.isForbidden) {
          setError('You do not have permission to add members to this group.');
        } else if (err.isNotFound) {
          setError('User not found.');
        } else if (err.isConflict) {
          setError('This user is already a member of the group.');
        } else {
          setError(err.message || 'Failed to add member.');
        }
      } else {
        setError('An unexpected error occurred while adding the member.');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Remove a member from a group
   */
  const removeMember = async (groupId: number, userId: number) => {
    try {
      setLoading(true);
      clearError();
      
      await groupService.removeMember(groupId, userId);
      
      // Remove from local members list
      members.value = members.value.filter(m => m.user_id !== userId);
      
    } catch (err) {
      if (err instanceof GroupError) {
        if (err.isForbidden) {
          if (currentGroup.value?.owner_id === userId) {
            setError('Cannot remove the group owner.');
          } else {
            setError('You do not have permission to remove members from this group.');
          }
        } else if (err.isNotFound) {
          setError('Member not found in this group.');
        } else {
          setError(err.message || 'Failed to remove member.');
        }
      } else {
        setError('An unexpected error occurred while removing the member.');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Update a member's role in a group
   */
  const updateMemberRole = async (groupId: number, userId: number, role: string) => {
    try {
      setLoading(true);
      clearError();
      
      // Note: API endpoint for updating member role needs to be implemented
      // For now, we'll use a placeholder
      // await groupService.updateMemberRole(groupId, userId, { role });
      
      // Update the local member's role
      const memberIndex = members.value.findIndex(m => m.user_id === userId);
      if (memberIndex !== -1) {
        members.value[memberIndex].role = role as 'admin' | 'member';
      }
      
      // For now, just refresh the members list
      await fetchMembers(groupId);
      
    } catch (err) {
      if (err instanceof GroupError) {
        if (err.isForbidden) {
          setError('You do not have permission to update member roles.');
        } else if (err.isNotFound) {
          setError('Member not found in this group.');
        } else {
          setError(err.message || 'Failed to update member role.');
        }
      } else {
        setError('An unexpected error occurred while updating the member role.');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Leave a group (current user removes themselves)
   */
  const leaveGroup = async (groupId: number) => {
    try {
      setLoading(true);
      clearError();
      
      const authStore = useAuthStore();
      const currentUserId = authStore.user?.id;
      
      if (!currentUserId) {
        throw new Error('User not authenticated');
      }
      
      await groupService.removeMember(groupId, currentUserId);
      
      // Remove the group from the list if we're leaving it
      groups.value = groups.value.filter(g => g.id !== groupId);
      
      // Clear current group if we're leaving it
      if (currentGroup.value?.id === groupId) {
        currentGroup.value = null;
        members.value = [];
      }
      
    } catch (err) {
      if (err instanceof GroupError) {
        if (err.isForbidden) {
          setError('You cannot leave this group.');
        } else {
          setError(err.message || 'Failed to leave group.');
        }
      } else {
        setError('An unexpected error occurred while leaving the group.');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Fetch members of a group
   * Note: Since the API doesn't have a dedicated members endpoint,
   * we assume members are included when fetching the group details
   */
  const fetchMembers = async (groupId: number) => {
    try {
      setLoading(true);
      clearError();
      
      // If we already have the current group with members, use them
      if (currentGroup.value?.id === groupId && currentGroup.value.members) {
        members.value = currentGroup.value.members;
        return currentGroup.value.members;
      }
      
      // Otherwise, fetch the group details which should include members
      const group = await groupService.getGroup(groupId);
      currentGroup.value = group;
      members.value = group.members || [];
      
      return members.value;
    } catch (err) {
      if (err instanceof GroupError) {
        setError(err.message || 'Failed to fetch group members.');
      } else {
        setError('An unexpected error occurred while fetching members.');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Clear the current group and members
   */
  const clearCurrentGroup = () => {
    currentGroup.value = null;
    members.value = [];
  };
  
  /**
   * Reset all filters and pagination
   */
  const resetFilters = () => {
    searchQuery.value = '';
    showPersonalGroups.value = true;
    sortBy.value = 'created_at';
    sortOrder.value = 'desc';
    currentPage.value = 1;
  };
  
  return {
    // State
    groups: readonly(groups),
    currentGroup: readonly(currentGroup),
    members: readonly(members),
    isLoading: readonly(isLoading),
    error: readonly(error),
    
    // Pagination
    currentPage,
    totalPages: readonly(totalPages),
    pageSize,
    totalItems: readonly(totalItems),
    
    // Filters
    searchQuery,
    showPersonalGroups,
    sortBy,
    sortOrder,
    
    // Getters
    personalGroups,
    regularGroups,
    filteredGroups,
    isGroupOwner,
    isGroupAdmin,
    canManageGroup,
    canDeleteGroup,
    
    // Actions
    fetchGroups,
    createGroup,
    fetchGroup,
    updateGroup,
    deleteGroup,
    addMember,
    removeMember,
    updateMemberRole,
    leaveGroup,
    fetchMembers,
    clearCurrentGroup,
    resetFilters,
    clearError,
    setLoading,
    setError
  };
});
